/* eslint-disable indent */
class SideTabs {
	constructor(el) {
		if (!el) return;

		if (el.dataset.sideTabsInit === '1') return;
		el.dataset.sideTabsInit = '1';

		this.el = el;

		this.tabsPanel = this.el.querySelector('.block-side-tabs__tabs');
		this.tabsContainer = this.el.querySelector('.block-side-tabs__tabs-container');

		this.tabContentCol = this.el.querySelector('.block-side-tabs__tab-content-col');
		this.tabContentWrapper = this.el.querySelector('.block-side-tabs__tab-content-wrapper');

		this.tabs = Array.from(this.el.querySelectorAll('.block-side-tabs__tab'));
		this.panels = Array.from(this.el.querySelectorAll('.block-side-tabs__tab-content-container'));

		this._panelHome = new Map();
		this.panels.forEach((panel) => {
			this._panelHome.set(panel, {
				parent: panel.parentNode,
				next: panel.nextSibling
			});
		});

		this._origCol = {
			parent: this.tabContentCol ? this.tabContentCol.parentNode : null,
			next: this.tabContentCol ? this.tabContentCol.nextSibling : null
		};

		this._lastIsDesktop = this.isDesktop();

		this.bindTabClicks();

		requestAnimationFrame(() => {
			this.applyResponsiveLayout();
			this.ensureActiveExists();
			this.syncTabsHeight();
			this.syncNoContentLayout();
			this.ensureMobileOpenUnderTab();
		});

		const debounce = (fn, wait = 150) => {
			let t;
			return (...args) => {
				clearTimeout(t);
				t = setTimeout(() => fn.apply(this, args), wait);
			};
		};

		this.onResize = debounce(() => {
			const isDesktopNow = this.isDesktop();
			const modeChanged = isDesktopNow !== this._lastIsDesktop;
			this._lastIsDesktop = isDesktopNow;

			requestAnimationFrame(() => {
				if (modeChanged) {
					this.applyResponsiveLayout();
					this.ensureActiveExists();
					this.ensureMobileOpenUnderTab();
				}

				this.syncTabsHeight();
				this.syncNoContentLayout();
			});
		}, 150);

		window.addEventListener('resize', this.onResize);
	}

	isDesktop = () => window.innerWidth >= 1025;

	setDesktopActiveByIndex = (idx) => {
		this.panels.forEach((panel) => {
			if (panel.dataset.index === idx) {
				panel.classList.add('active');
			} else {
				panel.classList.remove('active');
			}
		});

		this.tabs.forEach((t) => {
			if (t.dataset.index === idx) {
				t.classList.add('active');
			} else {
				t.classList.remove('active');
			}
		});
	};

	getMobileSlotForIndex = (idx) => {
		return this.el.querySelector(`.block-side-tabs__tab-accordion-slot[data-index="${idx}"]`);
	};

	closeAllMobile = () => {

		const slots = Array.from(this.el.querySelectorAll('.block-side-tabs__tab-accordion-slot'));
		slots.forEach((slot) => {
			const idx = slot.dataset.index;
			const panel = this.el.querySelector(`.block-side-tabs__tab-content-container[data-index="${idx}"]`);
			if (panel) this.restorePanelHome(panel);
			slot.remove();
		});

		this.tabs.forEach((t) => t.classList.remove('active'));
		this.panels.forEach((p) => p.classList.remove('active'));
	};

	restorePanelHome = (panel) => {
		const home = this._panelHome.get(panel);
		if (!home || !home.parent) return;

		if (panel.parentNode === home.parent) return;

		home.parent.insertBefore(panel, home.next);
	};

	openMobileByIndex = (idx) => {
		const tab = this.el.querySelector(`.block-side-tabs__tab[data-index="${idx}"]`);
		const panel = this.el.querySelector(`.block-side-tabs__tab-content-container[data-index="${idx}"]`);
		if (!tab || !panel) return;

		if (tab.classList.contains('is-empty')) return;

		// Toggle: if already open, close
		const alreadyOpen = tab.classList.contains('active') && !!this.getMobileSlotForIndex(idx);
		if (alreadyOpen) {
			this.closeAllMobile();
			return;
		}

		this.closeAllMobile();

		tab.classList.add('active');
		panel.classList.add('active');

		const slot = document.createElement('li');
		slot.className = 'block-side-tabs__tab-accordion-slot';
		slot.dataset.index = idx;

		tab.insertAdjacentElement('afterend', slot);

		slot.appendChild(panel);
	};

	ensureMobileOpenUnderTab = () => {
		if (this.isDesktop()) return;
		const hasAnySlot = !!this.el.querySelector('.block-side-tabs__tab-accordion-slot');
		if (hasAnySlot) return;

		let tab = this.el.querySelector('.block-side-tabs__tab.active:not(.is-empty)');
		if (!tab) tab = this.el.querySelector('.block-side-tabs__tab[data-index]:not(.is-empty)');
		if (!tab) return;

		this.openMobileByIndex(tab.dataset.index);
	};

	bindTabClicks = () => {
		this.tabs.forEach((tab) => {
			tab.addEventListener('click', (e) => {
				const idx = tab.dataset.index;
				if (!idx) return;

				e.preventDefault();

				if (this.isDesktop()) {
					this.setDesktopActiveByIndex(idx);

					requestAnimationFrame(() => {
						this.syncTabsHeight();
						this.syncNoContentLayout();
					});
					return;
				}

				this.openMobileByIndex(idx);
			});
		});
	};

	applyResponsiveLayout = () => {
		if (!this.tabsContainer || !this.tabsPanel || !this.tabContentCol || !this.tabContentWrapper) return;

		if (this.isDesktop()) {
			this.closeAllMobile();

			if (this._origCol.parent && !this._origCol.parent.contains(this.tabContentCol)) {
				this._origCol.parent.insertBefore(this.tabContentCol, this._origCol.next);
			}

			this.panels.forEach((panel) => this.restorePanelHome(panel));

			this.el.classList.remove('is-mobile-accordion');
		} else {
			this.el.classList.add('is-mobile-accordion');
		}
	};

	ensureActiveExists = () => {
		const activeTab = this.el.querySelector('.block-side-tabs__tab.active:not(.is-empty)');
		const activePanel = this.el.querySelector('.block-side-tabs__tab-content-container.active');

		if (activeTab && activePanel) return;

		const firstTab = this.el.querySelector('.block-side-tabs__tab[data-index]:not(.is-empty)');
		if (!firstTab) return;

		if (this.isDesktop()) {
			this.setDesktopActiveByIndex(firstTab.dataset.index);
		} else {

			this.openMobileByIndex(firstTab.dataset.index);
		}
	};

	getTabsTotalHeight = () => {
		if (!this.tabsPanel) return 0;

		const rect = this.tabsPanel.getBoundingClientRect();
		const cs = window.getComputedStyle(this.tabsPanel);

		const mt = parseFloat(cs.marginTop) || 0;
		const mb = parseFloat(cs.marginBottom) || 0;

		return Math.ceil(rect.height + mt + mb);
	};

	syncTabsHeight = () => {
		if (!this.tabsPanel) return;

		if (!this.isDesktop()) {
			this.el.style.removeProperty('--side-tabs-tabs-height');
			return;
		}

		const h = this.getTabsTotalHeight();
		this.el.style.setProperty('--side-tabs-tabs-height', `${h}px`);
	};

	getActiveContainer = () => {
		return this.el.querySelector('.block-side-tabs__tab-content-container.active');
	};

	isActiveNoContent = () => {
		const active = this.getActiveContainer();
		if (!active) return false;

		const contentWrap = active.querySelector('.block-side-tabs__tab-content-content-wrapper');
		if (!contentWrap) return false;

		const clone = contentWrap.cloneNode(true);

		const title = clone.querySelector('.block-side-tabs__tab-content-title');
		if (title) title.remove();

		const subtitle = clone.querySelector('.block-side-tabs__tab-content-subtitle');
		if (subtitle) subtitle.remove();

		const pm = clone.querySelector('.plus-minus');
		if (pm) pm.remove();

		const meaningfulEls = clone.querySelectorAll(
			'p, ul, ol, li, a, img, table, blockquote, hr, iframe, video, audio, form'
		);

		const remainingText = (clone.textContent || '').replace(/\s+/g, ' ').trim();

		return meaningfulEls.length === 0 && remainingText === '';
	};

	syncNoContentLayout = () => {
		if (!this.isDesktop()) {
			this.el.style.removeProperty('--side-tabs-panel-min-height');
			this.el.style.removeProperty('--side-tabs-tabs-mt');
			this.el.style.removeProperty('--side-tabs-tabs-mb');
			return;
		}

		if (!this.tabsPanel) return;

		const isNoContent = this.isActiveNoContent();

		if (!isNoContent) {
			this.el.style.removeProperty('--side-tabs-panel-min-height');
			this.el.style.removeProperty('--side-tabs-tabs-mt');
			this.el.style.removeProperty('--side-tabs-tabs-mb');
			return;
		}

		const tabsH = this.getTabsTotalHeight();
		const target = Math.ceil(tabsH * 1.3);

		this.el.style.setProperty('--side-tabs-panel-min-height', `${target}px`);
	};
}

export default SideTabs;